import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { API } from '../../api/client';

export default function AdminSettings() {
  const navigate = useNavigate();
  const [blurb, setBlurb] = useState('');
  const [original, setOriginal] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  // Program year — the single knob that drives every "current cycle" query.
  const [year, setYear] = useState<number>(0);
  const [origYear, setOrigYear] = useState<number>(0);
  const [availableYears, setAvailableYears] = useState<number[]>([]);
  const [savingYear, setSavingYear] = useState(false);
  const [yearSaved, setYearSaved] = useState(false);
  const [yearError, setYearError] = useState('');

  // Submission deadline — the date applicants see (and the countdown derives from).
  const [deadline, setDeadline] = useState('');
  const [origDeadline, setOrigDeadline] = useState('');
  const [savingDeadline, setSavingDeadline] = useState(false);
  const [deadlineSaved, setDeadlineSaved] = useState(false);
  const [deadlineError, setDeadlineError] = useState('');

  // Applications open — the date applications open for the active cohort year.
  const [openDate, setOpenDate] = useState('');
  const [origOpenDate, setOrigOpenDate] = useState('');
  const [savingOpenDate, setSavingOpenDate] = useState(false);
  const [openDateSaved, setOpenDateSaved] = useState(false);
  const [openDateError, setOpenDateError] = useState('');

  useEffect(() => {
    API.getSettings().then(data => {
      if (data) {
        setBlurb(data.programBlurb ?? '');
        setOriginal(data.programBlurb ?? '');
        setYear(data.currentYear ?? 0);
        setOrigYear(data.currentYear ?? 0);
        setAvailableYears(data.availableYears ?? []);
        setDeadline(data.submissionDeadline ?? '');
        setOrigDeadline(data.submissionDeadline ?? '');
        setOpenDate(data.applicationsOpen ?? '');
        setOrigOpenDate(data.applicationsOpen ?? '');
      }
      setLoading(false);
    });
  }, []);

  const saveOpenDate = async () => {
    if (openDate === origOpenDate) return;
    setSavingOpenDate(true);
    setOpenDateError('');
    try {
      const data = await API.updateSettings({ applicationsOpen: openDate });
      setOrigOpenDate(data?.applicationsOpen ?? openDate);
      setOpenDateSaved(true);
      setTimeout(() => setOpenDateSaved(false), 3000);
    } catch {
      setOpenDateError('Failed to update the open date. Please try again.');
    } finally {
      setSavingOpenDate(false);
    }
  };

  const saveDeadline = async () => {
    if (deadline === origDeadline) return;
    setSavingDeadline(true);
    setDeadlineError('');
    try {
      const data = await API.updateSettings({ submissionDeadline: deadline });
      setOrigDeadline(data?.submissionDeadline ?? deadline);
      setDeadlineSaved(true);
      setTimeout(() => setDeadlineSaved(false), 3000);
    } catch {
      setDeadlineError('Failed to update the deadline. Please try again.');
    } finally {
      setSavingDeadline(false);
    }
  };

  const saveYear = async () => {
    if (year === origYear) return;
    if (!window.confirm(
      `Set the program's current year to FY${year % 100}? This changes the active cohort across dashboards, FI priorities, calls, and the application form.`,
    )) return;
    setSavingYear(true);
    setYearError('');
    try {
      const data = await API.updateSettings({ currentYear: year });
      setOrigYear(data?.currentYear ?? year);
      if (data?.availableYears) setAvailableYears(data.availableYears);
      setYearSaved(true);
      setTimeout(() => setYearSaved(false), 3000);
    } catch {
      setYearError('Failed to update the program year. Please try again.');
    } finally {
      setSavingYear(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      await API.updateSettings({ programBlurb: blurb });
      setOriginal(blurb);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch {
      setError('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="phil-loading">Loading…</div>;

  const dirty = blurb !== original;

  return (
    <>
      <div className="phil-page-heading">Admin Settings</div>
      <div className="phil-page-sub">Manage program content, application questions, and FI priority data.</div>

      {/* Quick links to other admin sections. Split across rows so no single row
          gets too crowded; each card keeps a consistent 1/6 width. */}
      {[
        [
          { label: 'Application Questions', desc: 'Edit question labels, types, and options across all sections.', path: '/admin/questions', icon: '📋' },
          { label: 'Evaluator Questions', desc: 'Edit the scoring questions evaluators answer for each applicant.', path: '/admin/evaluator-questions', icon: '📝' },
          { label: 'Pipeline Columns', desc: 'Choose which applicant-answer columns appear in the pipeline table.', path: '/admin/pipeline-columns', icon: '🗂️' },
          { label: 'Priority Taxonomy', desc: 'Manage the L1–L3 master priority list used across calls, scoring and reports.', path: '/admin/priorities', icon: '🎯' },
          { label: 'Applicant Resources', desc: 'Upload decks, guides, and materials shown in the applicant Resources section.', path: '/admin/resources', icon: '📚' },
          { label: 'Team Access', desc: 'Add or remove FIL administrators who can reach the admin surface.', path: '/admin/team', icon: '👥' },
        ],
        [
          { label: 'Email Templates', desc: 'Edit the base outreach emails: scheduling, CAL briefing, and thank-you.', path: '/admin/email-templates', icon: '✉️' },
        ],
      ].map((row, ri) => (
        <div key={ri} style={{ display: 'flex', gap: 14, marginTop: ri === 0 ? 20 : 14, marginBottom: ri === 1 ? 28 : 0 }}>
          {row.map(item => (
            <div
              key={item.path}
              onClick={() => navigate(item.path)}
              style={{ flex: '0 1 calc((100% - 70px) / 6)', background: '#fff', border: '1px solid #e2e2e2', borderRadius: 8, padding: '16px 20px', cursor: 'pointer', transition: 'box-shadow 0.15s' }}
              onMouseEnter={e => (e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.08)')}
              onMouseLeave={e => (e.currentTarget.style.boxShadow = 'none')}
            >
              <div style={{ fontSize: 22, marginBottom: 8 }}>{item.icon}</div>
              <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4 }}>{item.label}</div>
              <div style={{ fontSize: 12, color: '#777', lineHeight: 1.5 }}>{item.desc}</div>
              <div style={{ marginTop: 10, fontSize: 12, color: '#00AEEF', fontWeight: 600 }}>Open →</div>
            </div>
          ))}
        </div>
      ))}

      <div className="phil-card" style={{ marginBottom: 20 }}>
        <div className="phil-card-head">
          <h2>Current Program Year</h2>
          <span style={{ fontSize: 12, color: '#888' }}>Drives every "current cycle" view</span>
        </div>
        <div style={{ padding: '20px 24px' }}>
          <div style={{ fontSize: 12, color: '#888', marginBottom: 10 }}>
            The active cohort year used across the dashboard, FI priorities, calls, and the
            application form. Change it to roll the program into a new fiscal year.
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <select
              className="phil-input"
              style={{ width: 140, padding: '6px 10px' }}
              value={year}
              onChange={e => { setYear(Number(e.target.value)); setYearSaved(false); }}
            >
              {availableYears.map(y => (
                <option key={y} value={y}>FY{y % 100} ({y})</option>
              ))}
            </select>
            <button
              className="phil-btn phil-btn-primary phil-btn-sm"
              onClick={saveYear}
              disabled={savingYear || year === origYear}
            >
              {savingYear ? 'Saving…' : 'Update Year'}
            </button>
            {yearSaved && <span style={{ fontSize: 12, color: '#28C840', fontWeight: 700 }}>Updated</span>}
          </div>
          {yearError && <div className="phil-error" style={{ marginTop: 12 }}>{yearError}</div>}

          <div style={{ borderTop: '1px solid #eee', margin: '20px 0 0' }} />
          <div style={{ fontWeight: 700, fontSize: 13, marginTop: 18, marginBottom: 6 }}>
            Submission Deadline{origYear ? ` — FY${origYear % 100}` : ''}
          </div>
          <div style={{ fontSize: 12, color: '#888', marginBottom: 10 }}>
            The application deadline applicants see on their dashboard, for the active
            program year{origYear ? ` (FY${origYear % 100})` : ''}. Also drives the
            "application window closes in N days" countdown. Each year keeps its own
            deadline. Leave blank to hide the countdown.
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <input
              type="date"
              className="phil-input"
              style={{ width: 180, padding: '6px 10px' }}
              value={deadline}
              onChange={e => { setDeadline(e.target.value); setDeadlineSaved(false); }}
            />
            <button
              className="phil-btn phil-btn-primary phil-btn-sm"
              onClick={saveDeadline}
              disabled={savingDeadline || deadline === origDeadline}
            >
              {savingDeadline ? 'Saving…' : 'Update Deadline'}
            </button>
            {deadline && (
              <button
                className="phil-btn phil-btn-secondary phil-btn-sm"
                onClick={() => { setDeadline(''); setDeadlineSaved(false); }}
              >
                Clear
              </button>
            )}
            {deadlineSaved && <span style={{ fontSize: 12, color: '#28C840', fontWeight: 700 }}>Updated</span>}
          </div>
          {deadlineError && <div className="phil-error" style={{ marginTop: 12 }}>{deadlineError}</div>}

          <div style={{ borderTop: '1px solid #eee', margin: '20px 0 0' }} />
          <div style={{ fontWeight: 700, fontSize: 13, marginTop: 18, marginBottom: 6 }}>
            Applications Open{origYear ? ` — FY${origYear % 100}` : ''}
          </div>
          <div style={{ fontSize: 12, color: '#888', marginBottom: 10 }}>
            The date applications open for the active program year
            {origYear ? ` (FY${origYear % 100})` : ''}. Each year keeps its own open
            date. Leave blank to leave it unset.
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <input
              type="date"
              className="phil-input"
              style={{ width: 180, padding: '6px 10px' }}
              value={openDate}
              onChange={e => { setOpenDate(e.target.value); setOpenDateSaved(false); }}
            />
            <button
              className="phil-btn phil-btn-primary phil-btn-sm"
              onClick={saveOpenDate}
              disabled={savingOpenDate || openDate === origOpenDate}
            >
              {savingOpenDate ? 'Saving…' : 'Update Open Date'}
            </button>
            {openDate && (
              <button
                className="phil-btn phil-btn-secondary phil-btn-sm"
                onClick={() => { setOpenDate(''); setOpenDateSaved(false); }}
              >
                Clear
              </button>
            )}
            {openDateSaved && <span style={{ fontSize: 12, color: '#28C840', fontWeight: 700 }}>Updated</span>}
          </div>
          {openDateError && <div className="phil-error" style={{ marginTop: 12 }}>{openDateError}</div>}
        </div>
      </div>

      <div className="phil-card">
        <div className="phil-card-head">
          <h2>Program Description</h2>
          <span style={{ fontSize: 12, color: '#888' }}>Shown on the applicant dashboard</span>
        </div>
        <div style={{ padding: '20px 24px' }}>
          <div className="phil-form-field">
            <div className="phil-q-label">Program Description</div>
            <div style={{ fontSize: 12, color: '#888', marginBottom: 8 }}>
              Shown as a tagline above the dashboard cards on the applicant home screen.
            </div>
            <textarea
              className="phil-textarea"
              style={{ height: 80 }}
              value={blurb}
              onChange={e => { setBlurb(e.target.value); setSaved(false); }}
              placeholder="e.g. 3 mo. program for FinTech & InsurTechs..."
            />
            <div style={{ fontSize: 11, color: '#aaa', marginTop: 4 }}>{blurb.length} characters</div>
          </div>

          <div style={{ background: '#f5f7fa', border: '1px solid #e2e2e2', borderRadius: 6, padding: '12px 16px', marginBottom: 20 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: 8 }}>
              Preview — as applicants see it
            </div>
            <div className="phil-program-blurb" style={{ marginBottom: 0 }}>
              {blurb || <span style={{ color: '#bbb', fontStyle: 'italic' }}>No description set</span>}
            </div>
          </div>

          {error && <div className="phil-error" style={{ marginBottom: 12 }}>{error}</div>}
          {saved && <div className="phil-saved-banner" style={{ marginBottom: 12 }}>Saved — applicants will see the updated description.</div>}

          <button
            className="phil-btn phil-btn-primary"
            onClick={handleSave}
            disabled={saving || !dirty}
          >
            {saving ? 'Saving…' : 'Save Changes'}
          </button>
          {dirty && !saving && (
            <button
              className="phil-btn phil-btn-secondary"
              style={{ marginLeft: 10 }}
              onClick={() => { setBlurb(original); setSaved(false); }}
            >
              Discard
            </button>
          )}
        </div>
      </div>
    </>
  );
}
